import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";

const client = generateClient<Schema>();

export interface FreepayRates {
  creditPercent: number;
  creditFixedCents: number;
  debitPercent: number;
  debitFixedCents: number;
  cashPercent: number;
  cashFixedCents: number;
}

export class PricingProfileRepository {
  /** Effective rates the terminal would resolve for this business (with source). */
  async getEffectiveRates(
    businessId: string,
  ): Promise<FreepayRates & { source: string | null }> {
    const { data, errors } = await client.queries.getEffectivePricingProfile({
      businessId,
    });
    if (errors?.length) throw new Error(errors[0].message);
    if (!data) throw new Error("No pricing profile returned");
    return {
      creditPercent: data.creditPercent,
      creditFixedCents: data.creditFixedCents,
      debitPercent: data.debitPercent,
      debitFixedCents: data.debitFixedCents,
      cashPercent: data.cashPercent,
      cashFixedCents: data.cashFixedCents,
      source: data.source ?? null,
    };
  }

  /** True when a per-business override record already exists. */
  async hasOverride(businessId: string): Promise<boolean> {
    const { data, errors } = await client.models.PricingProfile.get({
      id: businessId,
    });
    if (errors?.length) throw new Error(errors[0].message);
    return !!data;
  }

  /** Upsert the per-business override (id = businessId). */
  async saveBusinessRates(
    businessId: string,
    rates: FreepayRates,
  ): Promise<void> {
    const existing = await client.models.PricingProfile.get({ id: businessId });
    if (existing.errors?.length) throw new Error(existing.errors[0].message);
    if (existing.data) {
      const { errors } = await client.models.PricingProfile.update({
        id: businessId,
        ...rates,
      });
      if (errors?.length) throw new Error(errors[0].message);
    } else {
      const { errors } = await client.models.PricingProfile.create({
        id: businessId,
        ...rates,
      });
      if (errors?.length) throw new Error(errors[0].message);
    }
  }
}
